import React, { useEffect, useState } from "react";
import { Modal, Button, Form, Spinner } from "react-bootstrap";
import Select from "react-select";
import CloseIcon from "@material-ui/icons/Close";
import { useSelector } from "react-redux";
import { customSelectStyle } from "../../../../_metronic/_partials/dropdowns/ReactSelectCustomStyle";

const formatBRL = (value) =>
  typeof value === "number"
    ? value.toLocaleString("pt-BR", { style: "currency", currency: "BRL" })
    : "—";

function CardBrandIcon({ brand }) {
  const paymentMethodsRelation = useSelector(
    state => state.app.payment_methods_relation || []
  );
  const paymentMethod = paymentMethodsRelation.find(
    relation => relation.id === brand?.toLowerCase()
  );
  if (!paymentMethod) return null;
  return (
    <img alt={brand} src={paymentMethod.svg} style={{ height: "23px", width: "auto" }} />
  );
}

function CardOptionLabel({ card }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
      <CardBrandIcon brand={card.brand} />
      <span>{`${card.brand} •••• ${card.last_digits} — Val: ${card.expiration_date}`}</span>
    </div>
  );
}

function PreviewRow({ label, value, highlight }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", padding: "8px 0", borderBottom: "1px dashed #EBEDF3" }}>
      <span style={{ color: "#7E8299", fontSize: 14 }}>{label}</span>
      <span style={{ color: highlight ? "#01AB7D" : "#3F4254", fontWeight: 600, fontSize: 14 }}>{value}</span>
    </div>
  );
}

export default function UpgradePlanModal({
  show,
  onClose,
  onConfirm,
  loadPreview,
  creditCards = [],
  isLoadingCards = false,
  newPlanValue = 99
}) {
  const [preview, setPreview] = useState(null);
  const [isLoadingPreview, setIsLoadingPreview] = useState(false);
  const [previewError, setPreviewError] = useState(null);
  const [selectedCard, setSelectedCard] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (!show) return;
    let cancelled = false;
    setPreview(null);
    setPreviewError(null);
    setSelectedCard(null);
    setIsLoadingPreview(true);
    (async () => {
      try {
        const data = await loadPreview();
        if (!cancelled) setPreview(data);
      } catch (err) {
        if (!cancelled) setPreviewError(err);
      } finally {
        if (!cancelled) setIsLoadingPreview(false);
      }
    })();
    return () => { cancelled = true; };
  }, [show, loadPreview]);

  const handleClose = () => {
    if (isSubmitting) return;
    setPreview(null);
    setPreviewError(null);
    setSelectedCard(null);
    onClose();
  };

  const handleConfirm = async () => {
    if (!selectedCard || !preview?.canUpgrade) return;
    setIsSubmitting(true);
    try {
      await onConfirm({ cardId: selectedCard.hash, planValue: preview?.newPlanValue ?? newPlanValue });
    } catch (err) {
      console.error("Erro ao confirmar upgrade:", err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const isConfirmDisabled =
    isSubmitting || isLoadingPreview || !selectedCard || !preview?.canUpgrade;

  return (
    <Modal show={show} onHide={handleClose} centered>
      <Modal.Header>
        <Modal.Title>Upgrade da IA WhatsApp</Modal.Title>
        <span
          onClick={handleClose}
          aria-label="Fechar"
          style={{
            position: "absolute", top: "12px", right: "16px", cursor: "pointer",
            color: "#6c757d", borderRadius: "50%", width: "36px", height: "36px",
            display: "flex", alignItems: "center", justifyContent: "center",
            transition: "background 0.2s"
          }}
          onMouseEnter={e => e.currentTarget.style.background = "rgba(0,0,0,0.08)"}
          onMouseLeave={e => e.currentTarget.style.background = "none"}
        >
          <CloseIcon style={{ fontSize: "1.4rem" }} />
        </span>
      </Modal.Header>

      <Modal.Body>
        {isLoadingPreview && (
          <div style={{ display: "flex", justifyContent: "center", padding: "24px" }}>
            <Spinner animation="border" variant="primary" />
          </div>
        )}

        {!isLoadingPreview && previewError && (
          <div className="alert alert-custom alert-light-danger" role="alert">
            <div className="alert-text">
              Não foi possível carregar o preview do upgrade. Tente novamente.
            </div>
          </div>
        )}

        {!isLoadingPreview && preview && (
          <>
            <p style={{ color: "#7E8299", fontSize: 14 }}>
              Revise os valores do upgrade do plano antes de confirmar a cobrança.
            </p>

            <div style={{ background: "#F3F6F9", borderRadius: 8, padding: "12px 16px", marginBottom: 16 }}>
              <PreviewRow label="Plano atual" value={`${preview.currentPlan ?? "—"} (${formatBRL(preview.currentPlanValue)})`} />
              <PreviewRow label="Novo plano" value={`${preview.newPlan ?? "—"} (${formatBRL(preview.newPlanValue)})`} />
              <PreviewRow label="Dias usados" value={preview.daysUsed ?? "—"} />
              <PreviewRow label="Dias restantes" value={preview.daysRemaining ?? "—"} />
              <PreviewRow label="Crédito restante" value={formatBRL(preview.creditRemaining)} />
              <PreviewRow label="Valor a cobrar" value={formatBRL(preview.chargeValue)} highlight />
            </div>

            {!preview.canUpgrade && (
              <div className="alert alert-custom alert-light-warning" role="alert">
                <div className="alert-text">
                  {preview.message || "Upgrade não disponível no momento."}
                </div>
              </div>
            )}

            <Form.Group>
              <Form.Label>Cartão de crédito</Form.Label>
              <Select
                name="card"
                options={creditCards}
                value={selectedCard}
                onChange={setSelectedCard}
                isLoading={isLoadingCards}
                placeholder="Selecione um cartão..."
                getOptionLabel={(card) => `${card.brand} •••• ${card.last_digits} — Val: ${card.expiration_date}`}
                formatOptionLabel={(card) => <CardOptionLabel card={card} />}
                getOptionValue={(card) => card.hash}
                noOptionsMessage={() => <span>Nenhum cartão cadastrado.</span>}
                theme={customSelectStyle}
                isClearable={false}
                isDisabled={!preview.canUpgrade}
              />
            </Form.Group>
          </>
        )}
      </Modal.Body>

      <Modal.Footer>
        <Button variant="secondary" onClick={handleClose} disabled={isSubmitting}>
          Cancelar
        </Button>
        <Button variant="primary" onClick={handleConfirm} disabled={isConfirmDisabled}>
          {isSubmitting ? "Cobrando..." : "Cobrar e fazer upgrade"}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
